import { createSlice } from "@reduxjs/toolkit"

import { logout } from "../../LoginPage/redux/auth.actions"
import { PAYMENT_MATCHING_SLICE_NAME } from "./paymentMatching.constants"
import {
  deletePaymentMatchingIcon,
  getPaymentMatching,
  getPaymentMatchingFilterOptions,
  getPaymentMatchingIcons,
  getPaymentMatchingLogs,
  getPaymentMatchingLogUsers,
  getPaymentMatchingSummary,
  renamePaymentMatching,
  setPaymentMatchingIcon,
  uploadPaymentMatchingIcon
} from "./paymentMatching.thunks"

/* ------------- Constants ------------- */

// Exactly one block is always selected; there is no unselected state.
export const SUMMARY_BLOCKS = { ALL: "all", MAPPED: "mapped", UNMAPPED: "unmapped" }

// The multi-select dropdown uses "all" for "no restriction" and "no_value" for its explicit
// empty option, so both sentinels live here rather than being spelled out at every call site.
export const NO_RESTRICTION = "all"
export const NO_VALUE_OPTION = "no_value"

// What the API expects in place of a UI name to mean "rows that have none". The API advertises this
// value in its filter-options response and that answer wins; this default only covers the window
// before that response arrives, since the dropdown offers its empty option immediately.
export const NO_UI_NAME_FALLBACK = "__none__"

// Defaults of the log's three dropdowns. The date range is not All time on purpose: the log grows
// without limit and the common task is reviewing recent activity.
export const LOG_DEFAULT_FILTERS = { date_range: "last_30_days", field: "", user: "" }
export const LOG_PAGE_SIZE = 10

export const DEFAULT_ORDERING = "-transactions_count"
export const PAGE_SIZE = 25

/* ------------- Initial State ------------- */

const initialState = {
  fetching: false,
  result: { count: 0, next: null, previous: null, results: [] },

  summaryFetching: false,
  summary: {
    all_values: 0,
    mapped: 0,
    mapped_percent: 0,
    unmapped: 0,
    unmapped_percent: 0,
    counts_refreshed_at: null
  },

  filterOptions: { current_name: [], ui_name: [], payment_processor: [] },
  noUiNameValue: NO_UI_NAME_FALLBACK,

  block: SUMMARY_BLOCKS.ALL,
  filters: {
    current_name: NO_RESTRICTION,
    ui_name: NO_RESTRICTION,
    payment_processor: NO_RESTRICTION,
    no_icon: false
  },
  ordering: DEFAULT_ORDERING,
  page: 1,

  // The icon library only grows, so it is fetched once and kept for the popover's grid.
  iconLibrary: [],
  iconLibraryFetching: false,
  saveFetching: false,

  logs: { count: 0, results: [] },
  logsFetching: false,
  logUsers: []
}

/* ------------- Edits ------------- */

// Every edit shares one in-flight flag: only one row can be edited at a time, so there is never
// more than one of these running. Matchers keep that from being four identical triplets.
const editThunks = [
  renamePaymentMatching,
  setPaymentMatchingIcon,
  uploadPaymentMatchingIcon,
  deletePaymentMatchingIcon
]

const isEditPending = (action) => editThunks.some((thunk) => action.type === thunk.pending.type)

const isEditSettled = (action) =>
  editThunks.some(
    (thunk) => action.type === thunk.fulfilled.type || action.type === thunk.rejected.type
  )

/* ------------- Slice ------------- */

const slice = createSlice({
  name: PAYMENT_MATCHING_SLICE_NAME,
  initialState,
  reducers: {
    // The legacy action took two arguments - `paymentMatchingSetProp(key, value)`. RTK actions
    // carry a single payload, so callers pass `paymentMatchingSetProp({ key, value })`, matching
    // the `alertsSetProp`/`adminUsersSetProp` slices.
    paymentMatchingSetProp(state, { payload: { key, value } }) {
      if (key === "filters") {
        // Any filter change returns the view to the first page, otherwise a narrower result set
        // would keep showing page 4 of a list that no longer has one.
        state.filters = { ...state.filters, ...value }
        state.page = 1
        return
      }

      if (key === "block") {
        state.block = value
        state.page = 1
        return
      }

      state[key] = value
    },

    // Resets the filter bar and returns the summary block to All values, leaving sorting alone.
    clearPaymentMatchingFilters(state) {
      state.filters = initialState.filters
      state.block = initialState.block
      state.page = 1
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- table ---------------- */
      .addCase(getPaymentMatching.pending, (state) => {
        state.fetching = true
      })
      .addCase(getPaymentMatching.fulfilled, (state, { payload }) => {
        state.fetching = false
        state.result = payload
      })
      .addCase(getPaymentMatching.rejected, (state) => {
        state.fetching = false
      })

      .addCase(getPaymentMatchingSummary.pending, (state) => {
        state.summaryFetching = true
      })
      .addCase(getPaymentMatchingSummary.fulfilled, (state, { payload }) => {
        state.summaryFetching = false
        state.summary = payload
      })
      .addCase(getPaymentMatchingSummary.rejected, (state) => {
        state.summaryFetching = false
      })

      // The legacy reducer handled only the success phase: the filter bar keeps whatever options it
      // already has while a refresh is in flight, and a failure leaves them untouched.
      .addCase(getPaymentMatchingFilterOptions.fulfilled, (state, { payload }) => {
        state.filterOptions = {
          current_name: payload?.current_name || [],
          ui_name: payload?.ui_name || [],
          payment_processor: payload?.payment_processor || []
        }
        state.noUiNameValue = payload?.no_ui_name_value || NO_UI_NAME_FALLBACK
      })

      /* ---------------- activity log ---------------- */
      .addCase(getPaymentMatchingLogs.pending, (state) => {
        state.logsFetching = true
      })
      .addCase(getPaymentMatchingLogs.fulfilled, (state, { payload }) => {
        state.logsFetching = false
        state.logs = payload
      })
      .addCase(getPaymentMatchingLogs.rejected, (state) => {
        state.logsFetching = false
      })

      .addCase(getPaymentMatchingLogUsers.fulfilled, (state, { payload }) => {
        state.logUsers = payload || []
      })

      /* ---------------- icon library ---------------- */
      .addCase(getPaymentMatchingIcons.pending, (state) => {
        state.iconLibraryFetching = true
      })
      .addCase(getPaymentMatchingIcons.fulfilled, (state, { payload }) => {
        state.iconLibraryFetching = false
        state.iconLibrary = payload || []
      })
      .addCase(getPaymentMatchingIcons.rejected, (state) => {
        state.iconLibraryFetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)

      /* ---------------- edits ---------------- */
      // `getPaymentMatchingGroup` deliberately gets no cases, as in the legacy reducer: its result
      // is only ever read by the page to build a confirmation, never stored.
      .addMatcher(isEditPending, (state) => {
        state.saveFetching = true
      })
      .addMatcher(isEditSettled, (state) => {
        state.saveFetching = false
      })
  }
})

export const { paymentMatchingSetProp, clearPaymentMatchingFilters } = slice.actions

/* ------------- Selectors ------------- */

export const selectHasActiveFilters = (state) => {
  const { block, filters } = state[PAYMENT_MATCHING_SLICE_NAME]

  return (
    block !== SUMMARY_BLOCKS.ALL ||
    filters.no_icon ||
    [filters.current_name, filters.ui_name, filters.payment_processor].some(
      (value) => value === false || (Array.isArray(value) && value.length > 0)
    )
  )
}

export const reducer = slice.reducer
